/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import ChevronIcon from '../icons/Chevron';
import Accordion from '../partials/Accordion';
import IconLink from '../partials/IconLink';

import styles from '../../../styles/filters/alpha.module.scss';

const baseLetters = [
    // '#',
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'V',
    'W',
    'X',
    'Y',
    'Z',
];

const propTypes = {
    value: PropTypes.arrayOf(PropTypes.string),
    filter: PropTypes.shape({
        letters: PropTypes.arrayOf(PropTypes.string),
    }),
    onChange: PropTypes.func,
    className: PropTypes.string,
    isOpenClassName: PropTypes.string,
    itemWrapperClassName: PropTypes.string,
};

const defaultProps = {
    value: null,
    filter: null,
    onChange: null,
    className: null,
    isOpenClassName: null,
    itemWrapperClassName: null,
};

const Alpha = ({ value, filter, onChange, className, isOpenClassName, itemWrapperClassName }) => {
    const { letters = [], multiple = false, accordion = false, label = null } = filter || {};
    const currentValue = multiple ? value || [] : value || null;

    const onSelect = useCallback(
        (newValue) => {
            if (!multiple) {
                onChange(!currentValue || currentValue !== newValue ? newValue : null);
                return;
            }
            const newValues = [...currentValue];
            if (!currentValue.includes(newValue)) {
                newValues.push(newValue);
                onChange(newValues);
            } else {
                const filteredOptions = newValues.filter((op) => op !== newValue);
                onChange(filteredOptions);
            }
        },
        [onChange, filter, currentValue, multiple],
    );

    const onClickLetter = useCallback(
        (letter) => {
            if (letter !== null) {
                onSelect(letter);
            }
        },
        [onSelect],
    );

    const inner = (
        <div className={classNames([styles.container, { [className]: className !== null }])}>
            {baseLetters.map((letter) => {
                const valid = value !== null;
                const active = multiple
                    ? valid && (value || []).indexOf(letter) !== -1
                    : valid && value === letter;
                const available = (letters || []).indexOf(letter) !== -1;

                return (
                    <IconLink
                        className={classNames([
                            styles.letter,
                            { [styles.active]: active, [styles.disabled]: !available },
                        ])}
                        onClick={() => onClickLetter(letter)}
                        isDark
                        isCompact
                        key={`alpha-${letter}`}
                        disabled={!available}
                    >
                        {letter}
                    </IconLink>
                );
            })}
        </div>
    );

    return accordion ? (
        <Accordion
            label={
                <>
                    {label !== null ? (
                        label
                    ) : (
                        <FormattedMessage
                            defaultMessage="Alphabetical"
                            description="Filter label"
                        />
                    )}
                    {currentValue !== null && currentValue.length > 0 && multiple ? (
                        <div className={styles.activeFiltersBadge}>{currentValue.length}</div>
                    ) : null}
                    <ChevronIcon />
                </>
            }
            className={classNames([
                styles.accordion,
                {
                    [className]: className !== null,
                    [styles.hasSelected]: currentValue !== null && currentValue.length > 0,
                },
            ])}
            isOpenClassName={classNames([
                styles.isOpen,
                {
                    [isOpenClassName]: isOpenClassName !== null,
                },
            ])}
            titleClassName={styles.label}
            toggleClassName={styles.toggle}
            toggleButtonClassName={styles.toggleButton}
            itemWrapperClassName={classNames([
                styles.wrapper,
                {
                    [itemWrapperClassName]: itemWrapperClassName !== null,
                },
            ])}
            contentClassName={styles.content}
        >
            {inner}
        </Accordion>
    ) : (
        inner
    );
};

Alpha.propTypes = propTypes;
Alpha.defaultProps = defaultProps;

export default Alpha;
